import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Checkbox, Input, Select, Spin } from "antd";
import { Icon } from "Components";
import InfinityScrollWrapper from "Components/InfinityScroll/InfinityScrollWrapper";
import { DEFAULT_CAMPAIGNS_PAGESIZE } from "PhishingManager/PhishingManager.constants";

interface GroupOption {
    key: string;
    label: string;
    count: number;
    propertyKey: string;
}

interface PropertyOption {
    value: string;
    label: string;
    count?: number;
}

interface Props {
    propertyOptions: PropertyOption[];
    groupOptions: GroupOption[];
    selectedGroups: string[];
    setSelectedGroups: (groups: string[]) => void;
    groupSearchQuery: string;
    setGroupSearchQuery: (value: string) => void;
}

const GroupRecipients: React.FC<Props> = ({
    propertyOptions,
    groupOptions,
    selectedGroups,
    setSelectedGroups,
    groupSearchQuery,
    setGroupSearchQuery,
}) => {
    const [selectedProperties, setSelectedProperties] = useState<string[]>([]);
    const [items, setItems] = useState<PropertyOption[]>(propertyOptions);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [searchValue, setSearchValue] = useState("");
    const [allSelected, setAllSelected] = useState(false)
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const filteredItems = useMemo(() => {
        return items.filter(item =>
            item.label.toLowerCase().includes(searchValue.toLowerCase())
        );
    }, [items, searchValue]);

    const fetchItems = async (pageNumber: number) => {
        if (loading || !hasMore) return;
        setLoading(true);
        await new Promise((res) => setTimeout(res, 1000));
        const newItems = Array.from({ length: DEFAULT_CAMPAIGNS_PAGESIZE }, (_, i) => {
            const id = (pageNumber - 1) * DEFAULT_CAMPAIGNS_PAGESIZE + i + 1;
            return {
                value: `${id}`,
                label: `Property ${id}`,
            };
        });
        if (pageNumber === 5) setHasMore(false);
        setItems((prev) => [...prev, ...newItems]);
        setLoading(false);
    };

    const filteredGroups = useMemo(() => {
        return groupOptions.filter(
            (g) =>
                (allSelected || selectedProperties.includes(g.propertyKey)) &&
                g.label.toLowerCase().includes(groupSearchQuery.toLowerCase())
        );
    }, [groupOptions, selectedProperties, allSelected, groupSearchQuery]);

    const handleSelectAllGroups = () => {
        if (allGroupsSelected) {
            setSelectedGroups([]);
        } else {
            setSelectedGroups(filteredGroups.map((g) => g.key));
        }
    };

    const handleGroupToggle = (key: string) => {
        if (selectedGroups.includes(key)) {
            setSelectedGroups(selectedGroups.filter((g) => g !== key));
        } else {
            setSelectedGroups([...selectedGroups, key]);
        }
    };

    const handleSelectAllProperties = useCallback(() => {
        if (allSelected) {
            setAllSelected(false);
            setSelectedProperties([]);
            setSelectedGroups([]);
        } else {
            setAllSelected(true);
            setSelectedProperties(filteredItems.map((i) => i.value));
            setSearchValue("");
        }
    }, [allSelected, filteredItems]);

    const dropdownRender = useCallback(() => (
        <>
            <div className="group-recipients-all-option">
                <div className="group-recipients-option">
                    <Checkbox checked={allSelected} onClick={handleSelectAllProperties}>
                        Select All
                    </Checkbox>
                </div>
            </div>

            {dropdownOpen && (
                <>
                    {filteredItems.length === 0 && !loading ? (
                        <div className="group-recipients-option">No properties found</div>
                    ) : (
                        <InfinityScrollWrapper
                            hasMore={hasMore}
                            dataLength={filteredItems.length}
                            onLoadMore={() => {
                                if (!loading && hasMore) {
                                    const nextPage = page + 1;
                                    setPage(nextPage);
                                    fetchItems(nextPage);
                                }
                            }}
                            loading={!searchValue && loading}
                            height={200}
                            scrollableId="property-select-dropdown"
                            loaderText="Loading properties..."
                        >
                            {filteredItems.map((el, index) => (
                                <div className="group-recipients-option" key={el.value + index}>
                                    <Checkbox
                                        onClick={() => {
                                            if (selectedProperties.includes(el.value)) {
                                                setSelectedProperties(prev => prev.filter(v => v !== el.value));
                                                setSelectedGroups(selectedGroups.filter(groupId => {
                                                    const group = groupOptions.find(g => g.key === groupId);
                                                    return group?.propertyKey !== el.value;
                                                }));
                                            } else {
                                                setSelectedProperties(prev => [...prev, el.value]);
                                            }
                                        }}
                                        disabled={allSelected}
                                        checked={allSelected || selectedProperties.includes(el.value)}
                                    >
                                        {el.label}
                                    </Checkbox>
                                </div>
                            ))}
                        </InfinityScrollWrapper>
                    )}
                </>
            )}
        </>
    ), [dropdownOpen, filteredItems, allSelected, selectedProperties, hasMore, loading, page, searchValue, handleSelectAllProperties]);

    const sharedProps = useMemo(() => ({
        mode: 'multiple' as const,
        style: { width: '100%' },
        options: items,
        placeholder: 'Select Item...',
        size: "large" as const,
        maxTagCount: 0,
        showSearch: !allSelected,
        tagRender: () => <span />,
        maxTagPlaceholder: () => (
            <span>
                {allSelected ? "All Properties" : `${selectedProperties.length} selected`}
            </span>
        ),
    }), [items, allSelected, selectedProperties.length]);

    const selectProps = useMemo(() => ({
        value: selectedProperties,
        onSearch: (val: string) => setSearchValue(val),
    }), [selectedProperties]);

    const totalCount = groupOptions
        .filter((g) => selectedGroups.includes(g.key))
        .reduce((acc, g) => acc + g.count, 0);

    const allGroupsSelected =
        filteredGroups.length > 0 &&
        filteredGroups.every((g) => selectedGroups.includes(g.key));

    useEffect(() => {
        fetchItems(1);
    }, []);

    return (
        <div className="phishing-step3__role-section">
            <div className="phishing-step3__role-filters">
                <div className="phishing-step3__filter-group">
                    <div className="phishing-step3__label">
                        Select property <span className="required">*</span>
                    </div>
                    <Select
                        className="phishing-step3__properties_select"
                        {...sharedProps}
                        {...selectProps}
                        onDropdownVisibleChange={(open) => {
                            setDropdownOpen(open);
                            if (!open) {
                                setSearchValue("");
                            }
                        }}
                        getPopupContainer={(trigger) => trigger.parentElement}
                        dropdownRender={dropdownRender}
                    />
                </div>

                <div className="phishing-step3__filter-group phishing-step3__role-search">
                    <div>
                        <Input.Search
                            placeholder="Search groups"
                            value={groupSearchQuery}
                            onChange={(e: any) => setGroupSearchQuery(e.target.value)}
                            suffix={<Icon type="search" />}
                        />
                    </div>
                </div>
            </div>

            <div className="phishing-step3__role-list-header">
                <div className="phishing-step3__label">
                    Select a group
                    {totalCount > 0 && (
                        <>
                            <span className="phishing-step3__dot"> · </span>
                            <span className="phishing-step3__total-count">
                                {totalCount} users selected <span className="required">*</span>
                            </span>
                        </>
                    )}
                </div>

                <button
                    className="phishing-step3__select-all"
                    onClick={handleSelectAllGroups}
                >
                    {allGroupsSelected ? "Deselect all" : "Select all"}
                </button>
            </div>

            <div className="phishing-step3__role-list">
                {selectedProperties.length === 0 ? (
                    <div className="phishing-step3__role-item phishing-step3__role-item--empty">
                        Please select a property to see groups.
                    </div>
                ) : filteredGroups.length === 0 ? (
                    <div className="phishing-step3__role-item phishing-step3__role-item--empty">
                        No groups found.
                    </div>
                ) : (<InfinityScrollWrapper
                    dataLength={filteredGroups.length}
                    hasMore={false}
                    endMessageText=""
                    onLoadMore={() => { console.log("Load more..") }}
                    loading={loading}
                    height={400}
                    scrollableId="group-list-infinite-scroll"
                    loaderText="Loading groups..."
                >
                    {filteredGroups.map((group) => (
                        <div
                            key={group.key}
                            className="phishing-step3__role-item"
                            onClick={() => handleGroupToggle(group.key)}
                        >
                            <Checkbox
                                checked={selectedGroups.includes(group.key)}
                                onChange={() => handleGroupToggle(group.key)}
                                onClick={(e) => e.stopPropagation()}
                            />
                            <div className="phishing-step3__role-info">
                                <div className="phishing-step3__role-title">
                                    <Icon type="recipientGroup" />
                                    {group.label}
                                </div>
                                <span className="phishing-step3__role-desc">
                                    {group.count} users
                                </span>
                            </div>
                        </div>
                    ))}
                </InfinityScrollWrapper>
                )}
            </div>
        </div>
    );
};

export default GroupRecipients;